// Notary Public (ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร) × province landings — local intent.
// กรอบวิชาชีพ: ข้อบังคับสภาทนายความว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร พ.ศ. 2551
// ประเทศไทยยังไม่ได้เป็นภาคีอนุสัญญากรุงเฮก (Apostille) จนถึงปี 2026 — เอกสารใช้ต่างประเทศต้องผ่าน
// นิติกรณ์กรมการกงสุล กระทรวงการต่างประเทศ แล้วจึงรับรองต่อที่สถานทูต/สถานกงสุลของประเทศปลายทาง
import { getProvinceBySlug, type ThaiProvince } from "@/content/provinces-th/registry";

export interface NotaryProvinceLanding {
  slug: string;
  provinceTh: string;
  provinceEn: string;
  /** ทำไมความต้องการทนายรับรองเอกสารในพื้นที่นี้สูง */
  demandTh: string;
  demandEn: string;
  /** เอกสารที่ถูกขอรับรองบ่อยที่สุดในพื้นที่ */
  topDocsTh: string[];
  topDocsEn: string[];
  /** วิธีนัดหมาย / รับ-ส่งเอกสาร */
  logisticsTh: string;
  logisticsEn: string;
  turnaroundTh: string;
  turnaroundEn: string;
}

const P = (
  slug: string,
  demandTh: string,
  demandEn: string,
  topDocsTh: string[],
  topDocsEn: string[],
  logisticsTh: string,
  logisticsEn: string,
  turnaroundTh: string,
  turnaroundEn: string,
): NotaryProvinceLanding => {
  const p = getProvinceBySlug(slug) as ThaiProvince;
  return { slug, provinceTh: p.nameTh, provinceEn: p.nameEn, demandTh, demandEn, topDocsTh, topDocsEn, logisticsTh, logisticsEn, turnaroundTh, turnaroundEn };
};

const CORE_TH = [
  "หนังสือมอบอำนาจ (Power of Attorney)",
  "คำให้การ / Affidavit",
  "รับรองสำเนาถูกต้องของหนังสือเดินทางและบัตรประชาชน",
  "หนังสือยินยอมให้บุตรเดินทางไปต่างประเทศ",
];
const CORE_EN = [
  "Power of Attorney",
  "Affidavit / statutory declaration",
  "Certified true copies of passport and Thai ID",
  "Parental consent letter for a child travelling abroad",
];

const DEFAULT_TURN_TH = "รับรองต่อหน้าทนายเสร็จภายในวันเดียว (นัดล่วงหน้า) · หากต้องทำนิติกรณ์กรมการกงสุลต่อ เพิ่มอีก 2–3 วันทำการ (บริการด่วนภายในวันเดียวมีค่าธรรมเนียมเพิ่ม) · รับรองที่สถานทูตปลายทางขึ้นกับคิวของแต่ละสถานทูต";
const DEFAULT_TURN_EN = "Notarisation is completed the same day by appointment; add 2–3 working days if MFA legalisation is required (same-day express available at extra cost). Embassy attestation timing depends on each mission's queue.";

export const NOTARY_PROVINCE_LANDINGS: NotaryProvinceLanding[] = [
  P(
    "bangkok",
    "กรุงเทพฯ เป็นที่ตั้งของกองสัญชาติและนิติกรณ์ กรมการกงสุล (แจ้งวัฒนะ) และสถานทูตเกือบทุกประเทศ งานรับรองเอกสารส่วนใหญ่จึงเป็นการเดินเรื่องต่อเนื่อง ทนายรับรอง → นิติกรณ์ MFA → สถานทูตปลายทาง ภายในเมืองเดียว",
    "Bangkok hosts the MFA Legalisation Division at Chaeng Watthana and almost every foreign embassy, so most work here runs end-to-end in one city: attorney notarisation, MFA legalisation, then embassy attestation.",
    [...CORE_TH, "เอกสารบริษัท (หนังสือรับรอง DBD, บอจ.5, มติที่ประชุม)", "แบบฟอร์มธนาคารต่างประเทศและตัวอย่างลายมือชื่อ"],
    [...CORE_EN, "Corporate documents (DBD affidavit, shareholder list, board resolutions)", "Foreign bank forms and specimen signature verification"],
    "นัดเข้าพบที่สำนักงานย่านกรุงเทพฯ หรือให้ทนายเดินทางไปรับรองนอกสถานที่ในเขตกรุงเทพฯ–ปริมณฑล และเดินเรื่องนิติกรณ์ให้ต่อโดยไม่ต้องไปเอง",
    "Meet at our Bangkok office, or have the attorney travel to you anywhere in Bangkok and the surrounding provinces; we can then handle MFA legalisation on your behalf.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "phuket",
    "ภูเก็ตมีชาวต่างชาติพำนักระยะยาวและเจ้าของอสังหาริมทรัพย์จำนวนมาก งานรับรองส่วนใหญ่เป็นหนังสือมอบอำนาจขายบ้าน/คอนโดในต่างประเทศ เอกสารยืนยันตัวตนสำหรับธนาคารต่างประเทศ และเอกสารสมรสไทย–ต่างชาติที่อำเภอเมืองภูเก็ต ถลาง และกะทู้",
    "Phuket has a large long-stay foreign resident and property-owning community. Typical work includes POAs to sell property overseas, identity verification for foreign banks, and Thai–foreign marriage paperwork filed at Mueang Phuket, Thalang and Kathu district offices.",
    [...CORE_TH, "หนังสือมอบอำนาจขายอสังหาริมทรัพย์ในต่างประเทศ", "แบบฟอร์มยืนยันตัวตน KYC ของธนาคาร/โบรกเกอร์ต่างประเทศ"],
    [...CORE_EN, "POA to sell overseas real estate", "KYC identity verification forms for foreign banks and brokers"],
    "เข้าพบทีมงานที่สำนักงานภูเก็ตได้โดยตรง หรือส่งร่างเอกสารมาตรวจล่วงหน้าทางอีเมลก่อนนัดเซ็น (ห้ามลงลายมือชื่อมาก่อน) และให้เราส่งเรื่องต่อกรมการกงสุลที่กรุงเทพฯ ให้",
    "Visit our Phuket office in person, or email the draft for review before your signing appointment (do not pre-sign). We can forward the file to the MFA in Bangkok for you.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "chonburi",
    "ชลบุรี–พัทยา–ศรีราชามีทั้งชุมชนชาวต่างชาติเกษียณอายุและนิคมอุตสาหกรรมภาคตะวันออก งานรับรองจึงมีทั้งเอกสารส่วนบุคคลของชาวต่างชาติและเอกสารบริษัทของผู้ผลิตญี่ปุ่น/ยุโรปใน EEC",
    "Chonburi — Pattaya and Sriracha — combines a retiree expat community with the Eastern Seaboard industrial estates, so notarial work spans personal expat documents and corporate paperwork for Japanese and European manufacturers in the EEC.",
    [...CORE_TH, "มติที่ประชุมและหนังสือมอบอำนาจของบริษัทในนิคมอุตสาหกรรม", "สัญญาข้ามประเทศและเอกสารประกอบการยื่นต่อหน่วยงานต่างประเทศ"],
    [...CORE_EN, "Board resolutions and corporate POAs for industrial-estate companies", "Cross-border contracts and supporting documents for foreign authorities"],
    "นัดหมายที่ศรีราชา/พัทยา หรือให้ทนายเข้าไปรับรองที่โรงงานหรือสำนักงานในนิคมอุตสาหกรรมได้ตามรอบที่นัดไว้",
    "Appointments in Sriracha or Pattaya, or the attorney can attend your factory or office inside an industrial estate on a scheduled visit.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "chiang-mai",
    "เชียงใหม่เป็นศูนย์กลางของชาวต่างชาติทำงานทางไกล นักศึกษาต่างชาติ และครอบครัวไทย–ต่างชาติภาคเหนือ งานรับรองที่พบบ่อยคือเอกสารการศึกษา หนังสือยินยอมให้บุตรเดินทาง และหนังสือมอบอำนาจจัดการทรัพย์สินในต่างประเทศ",
    "Chiang Mai is a hub for remote workers, international students and Thai–foreign families in the north. Common work: education documents, parental travel consent letters and POAs to manage assets abroad.",
    [...CORE_TH, "รับรองสำเนาทรานสคริปต์และใบปริญญาเพื่อยื่นมหาวิทยาลัยต่างประเทศ", "คำให้การรับรองสถานะการเงิน/ที่พักสำหรับวีซ่า"],
    [...CORE_EN, "Certified copies of transcripts and degrees for overseas universities", "Affidavits on finances or accommodation for visa applications"],
    "นัดหมายในตัวเมืองเชียงใหม่ หรือส่งร่างเอกสารตรวจก่อนแล้วเข้าพบเซ็นครั้งเดียวจบ พร้อมให้เราเดินเรื่องนิติกรณ์ที่กรุงเทพฯ ต่อให้",
    "Book an appointment in the city, or send drafts for review first and sign in a single visit while we handle the Bangkok legalisation steps.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "nonthaburi",
    "นนทบุรีอยู่ติดกรมการกงสุล แจ้งวัฒนะ ทำให้เป็นพื้นที่ที่ผู้ยื่นนิติกรณ์ค้นหาทนายรับรองเอกสารมากที่สุดแห่งหนึ่ง โดยเฉพาะผู้ที่ถูกเจ้าหน้าที่แจ้งว่าต้องให้ทนายรับรองลายมือชื่อก่อนจึงจะรับนิติกรณ์ได้",
    "Nonthaburi sits next to the MFA Legalisation Division at Chaeng Watthana, making it one of the highest-intent areas for notarial services — especially for applicants told that a lawyer's signature certification is required before legalisation.",
    [...CORE_TH, "เอกสารเอกชนที่ต้องมีลายมือชื่อทนายก่อนยื่นนิติกรณ์"],
    [...CORE_EN, "Private documents requiring an attorney's certification before MFA legalisation"],
    "นัดรับรองใกล้แจ้งวัฒนะแล้วยื่นนิติกรณ์ต่อได้ในวันเดียวกัน หรือมอบให้เราเดินเรื่องยื่นแทนทั้งกระบวนการ",
    "Notarise near Chaeng Watthana and lodge for legalisation the same day, or let us run the whole process for you.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "samut-prakan",
    "สมุทรปราการเป็นที่ตั้งของท่าอากาศยานสุวรรณภูมิและโรงงานส่งออกจำนวนมาก งานรับรองมักเป็นเอกสารบริษัทเพื่อการค้าระหว่างประเทศและเอกสารส่วนบุคคลของแรงงานที่จะไปทำงานต่างประเทศ",
    "Samut Prakan hosts Suvarnabhumi Airport and a dense export-manufacturing base; work here is typically corporate documents for international trade plus personal documents for workers heading overseas.",
    [...CORE_TH, "หนังสือรับรองบริษัทและเอกสารการค้าระหว่างประเทศ", "เอกสารประกอบสัญญาจ้างงานต่างประเทศ"],
    [...CORE_EN, "Company affidavits and international trade documents", "Supporting documents for overseas employment contracts"],
    "นัดหมายในพื้นที่สมุทรปราการหรือกรุงเทพฯ ฝั่งตะวันออก และให้เรานำเรื่องเข้ากรมการกงสุลต่อให้",
    "Appointments in Samut Prakan or eastern Bangkok, with onward MFA lodgement handled by us.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "pathum-thani",
    "ปทุมธานีมีมหาวิทยาลัยและนิคมอุตสาหกรรมนวนคร งานรับรองส่วนใหญ่จึงเป็นเอกสารการศึกษาเพื่อสมัครเรียนต่อต่างประเทศ และเอกสารบริษัทของผู้ผลิตในพื้นที่",
    "Pathum Thani is home to major universities and the Navanakorn industrial estate, so notarial work centres on education documents for overseas study and corporate documents for local manufacturers.",
    [...CORE_TH, "รับรองสำเนาทรานสคริปต์ ใบปริญญา และใบรับรองผลการศึกษา"],
    [...CORE_EN, "Certified copies of transcripts, degrees and academic records"],
    "นัดหมายในพื้นที่รังสิต/นวนคร หรือเข้าพบที่สำนักงานกรุงเทพฯ ซึ่งใกล้กรมการกงสุลมากกว่า",
    "Appointments around Rangsit or Navanakorn, or meet at our Bangkok office which is closer to the MFA.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "rayong",
    "ระยองเป็นหัวใจของ EEC และมาบตาพุด มีบริษัทต่างชาติจำนวนมากที่ต้องรับรองเอกสารเพื่อส่งกลับสำนักงานใหญ่ รวมถึงพนักงานต่างชาติที่ต้องรับรองเอกสารส่วนตัว",
    "Rayong is the heart of the EEC and Map Ta Phut, with many foreign-invested companies certifying documents for head office, plus expatriate staff certifying personal paperwork.",
    [...CORE_TH, "มติที่ประชุมผู้ถือหุ้นและหนังสือมอบอำนาจสำหรับสำนักงานใหญ่ต่างประเทศ"],
    [...CORE_EN, "Shareholder resolutions and POAs for overseas head offices"],
    "ให้ทนายเดินทางเข้ารับรองที่สำนักงานในระยอง/มาบตาพุดตามรอบนัด หรือส่งเอกสารเข้ากรุงเทพฯ ให้เราดำเนินการ",
    "The attorney can attend your Rayong or Map Ta Phut office on a scheduled visit, or send documents to Bangkok for us to process.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "khon-kaen",
    "ขอนแก่นเป็นศูนย์กลางอีสาน มีครอบครัวไทย–ต่างชาติและผู้ไปทำงานต่างประเทศจำนวนมาก งานที่พบบ่อยคือหนังสือมอบอำนาจให้ญาติจัดการทรัพย์สินแทน และหนังสือยินยอมให้บุตรเดินทาง",
    "Khon Kaen anchors the northeast, with many Thai–foreign families and overseas workers. Typical work: POAs authorising relatives to manage assets, and parental consent letters for children travelling.",
    [...CORE_TH, "หนังสือมอบอำนาจให้ญาติจัดการทรัพย์สิน/ที่ดินแทน"],
    [...CORE_EN, "POAs authorising relatives to manage property or land"],
    "นัดหมายในตัวเมืองขอนแก่น หรือส่งร่างเอกสารตรวจล่วงหน้าและนัดเซ็นครั้งเดียว พร้อมให้เราเดินเรื่องนิติกรณ์ที่กรุงเทพฯ",
    "Appointments in Khon Kaen city, or send drafts ahead and sign in one visit while we handle Bangkok legalisation.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "udon-thani",
    "อุดรธานีมีคู่สมรสไทย–ต่างชาติหนาแน่นที่สุดจังหวัดหนึ่งของภาคอีสาน งานรับรองจึงเน้นเอกสารสมรส เอกสารบำนาญ/สวัสดิการต่างประเทศ และหนังสือมอบอำนาจ",
    "Udon Thani has one of the highest concentrations of Thai–foreign couples in the northeast, so work focuses on marriage paperwork, foreign pension and benefit forms, and POAs.",
    [...CORE_TH, "แบบฟอร์มบำนาญ/ประกันสังคมต่างประเทศที่ต้องมีพยานรับรองลายมือชื่อ"],
    [...CORE_EN, "Foreign pension and social-security forms requiring witnessed signatures"],
    "นัดหมายในตัวเมืองอุดรธานี หรือส่งเอกสารทางไปรษณีย์ลงทะเบียนหลังยืนยันรูปแบบเอกสารกับเราแล้ว",
    "Appointments in Udon Thani city, or post documents by registered mail once we have confirmed the required format.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "nakhon-ratchasima",
    "นครราชสีมาเป็นประตูสู่อีสานและมีนิคมอุตสาหกรรมสุรนารี งานรับรองมีทั้งเอกสารบริษัทและเอกสารส่วนบุคคลสำหรับผู้ไปทำงาน/เรียนต่อต่างประเทศ",
    "Nakhon Ratchasima is the gateway to the northeast and home to the Suranaree industrial estate, generating both corporate and personal notarial work for overseas work and study.",
    [...CORE_TH, "เอกสารบริษัทและสัญญาที่ต้องใช้ต่างประเทศ"],
    [...CORE_EN, "Corporate documents and contracts intended for use abroad"],
    "นัดหมายในตัวเมืองโคราช หรือใช้ช่องทางส่งเอกสารเข้าสำนักงานกรุงเทพฯ เพื่อความรวดเร็วในการทำนิติกรณ์",
    "Appointments in Korat city, or route documents to our Bangkok office for faster legalisation.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "ubon-ratchathani",
    "อุบลราชธานีเป็นพื้นที่ที่มีผู้เดินทางไปทำงานต่างประเทศและครอบครัวไทย–ต่างชาติจำนวนมาก งานรับรองเน้นหนังสือมอบอำนาจ คำให้การ และเอกสารยืนยันตัวตน",
    "Ubon Ratchathani sends many workers abroad and hosts numerous Thai–foreign families; work focuses on POAs, affidavits and identity verification.",
    CORE_TH,
    CORE_EN,
    "นัดหมายในตัวเมืองอุบลฯ หรือประสานงานทางออนไลน์ก่อนแล้วส่งเอกสารต้นฉบับตามมา",
    "Appointments in Ubon city, or coordinate online first and forward the originals afterwards.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "songkhla",
    "สงขลา–หาดใหญ่เป็นศูนย์กลางการค้าชายแดนไทย–มาเลเซีย งานรับรองมักเกี่ยวข้องกับเอกสารธุรกิจข้ามแดน การศึกษาต่อในมาเลเซีย/สิงคโปร์ และหนังสือมอบอำนาจ",
    "Songkhla and Hat Yai anchor Thai–Malaysian border trade; work often involves cross-border business documents, study applications to Malaysia or Singapore, and POAs.",
    [...CORE_TH, "เอกสารธุรกิจข้ามแดนไทย–มาเลเซีย"],
    [...CORE_EN, "Cross-border Thai–Malaysian business documents"],
    "นัดหมายในหาดใหญ่ หรือส่งร่างเอกสารตรวจล่วงหน้าเพื่อลดจำนวนครั้งที่ต้องเดินทาง",
    "Appointments in Hat Yai, or send drafts in advance to reduce the number of trips.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "surat-thani",
    "สุราษฎร์ธานีครอบคลุมเกาะสมุย เกาะพะงัน และเกาะเต่า ซึ่งมีเจ้าของธุรกิจและผู้พำนักชาวต่างชาติจำนวนมากที่ต้องรับรองเอกสารเพื่อใช้ในประเทศบ้านเกิด",
    "Surat Thani covers Koh Samui, Koh Phangan and Koh Tao, where many foreign residents and business owners need documents certified for use in their home countries.",
    [...CORE_TH, "เอกสารยืนยันที่อยู่และตัวตนสำหรับหน่วยงานต่างประเทศ"],
    [...CORE_EN, "Address and identity verification documents for foreign authorities"],
    "ประสานงานล่วงหน้าเพื่อจัดรอบเข้าพบบนเกาะสมุย หรือส่งเอกสารเข้าสำนักงานภูเก็ต/กรุงเทพฯ",
    "Arrange a scheduled visit on Koh Samui in advance, or send documents to our Phuket or Bangkok office.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
  P(
    "krabi",
    "กระบี่มีชาวต่างชาติพำนักระยะยาวและธุรกิจท่องเที่ยวที่ต้องใช้เอกสารรับรองเพื่อธุรกรรมในต่างประเทศ เช่น การขายทรัพย์สินและการจัดการบัญชีธนาคารต่างประเทศ",
    "Krabi's long-stay foreign residents and tourism businesses regularly need certified documents for overseas transactions such as property sales and foreign bank accounts.",
    CORE_TH,
    CORE_EN,
    "จัดรอบเข้าพบในตัวเมืองกระบี่/อ่าวนาง หรือเดินทางเข้าพบที่สำนักงานภูเก็ตซึ่งใกล้ที่สุด",
    "Scheduled visits in Krabi town or Ao Nang, or meet at our Phuket office — the nearest permanent location.",
    DEFAULT_TURN_TH,
    DEFAULT_TURN_EN,
  ),
];

export function getNotaryProvinceLanding(slug: string): NotaryProvinceLanding | undefined {
  return NOTARY_PROVINCE_LANDINGS.find((p) => p.slug === slug);
}
